import * as React from "react";
import { FieldValues, Path, useFormContext } from "react-hook-form";
import { useLanguage } from "../../Language";
import { BorrowerNotificationType } from "@tlc/ls2pac/dto/BorrowerNotificationType";
import { Box, Button, IconButton, Stack } from "@mui/material";
import { EmailField } from "./EmailField";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { useWatchWithCallback } from "@tlc/forms/FormTextField";

export const isFieldRequiredByNotification = (
  notificationTypes: BorrowerNotificationType[][],
  fieldType: BorrowerNotificationType,
) => (notificationTypes || []).some((type) => (type || []).includes(fieldType));

export interface AdditionalEmailGroupProps<T extends FieldValues> {
  disabled?: boolean;
  email2?: Path<T> | null;
  email3?: Path<T> | null;
}

export const AdditionalEmailGroup = <T extends FieldValues>({
  disabled = false,
  email2,
  email3,
}: AdditionalEmailGroupProps<T>) => {
  const { translate } = useLanguage();
  const { setValue, trigger, getValues, getFieldState } = useFormContext<T>();
  const { isTouched: isEmail3Touched } = getFieldState(email2);

  const [showEmail2, setShowEmail2] = React.useState(
    email2 ? !!getValues(email2) : false,
  );
  const [isEmail3Filled, setEmail3Filled] = React.useState(
    email3 ? !!getValues(email3) : false,
  );
  const [showEmail3, setShowEmail3] = React.useState(isEmail3Filled);

  if (email3) {
    useWatchWithCallback(email3, (value) => {
      const filled = !!value;
      setEmail3Filled(!!value);
      if (isEmail3Filled !== filled && isEmail3Touched) {
        trigger(email2);
      }
    });
  }

  return (
    <>
      {showEmail2 && (
        <Stack direction={"row"}>
          <EmailField<T>
            disabled={disabled}
            name={email2}
            sx={{
              maxWidth: { sm: "320px", md: "648px" },
              flexGrow: 1,
            }}
            label={translate("ci_email2")}
            options={{
              required:
                isEmail3Filled && translate("email2RequiredWhenEmail3Present"),
            }}
          ></EmailField>
          <Box sx={{ pt: 1 }}>
            <IconButton
              onClick={() => {
                setValue(email2, null);
                setShowEmail2(false);
              }}
              disabled={showEmail3 || disabled}
              aria-label={translate("delete")}
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        </Stack>
      )}
      {showEmail3 && (
        <Stack direction={"row"}>
          <EmailField<T>
            disabled={disabled}
            sx={{
              maxWidth: { sm: "320px", md: "648px" },
              flexGrow: 1,
            }}
            name={email3}
            label={translate("ci_email3")}
          ></EmailField>
          <Box sx={{ pt: 1 }}>
            <IconButton
              disabled={disabled}
              aria-label={translate("delete")}
              onClick={() => {
                setValue(email3, null);
                setShowEmail3(false);
              }}
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        </Stack>
      )}
      {((!showEmail3 && !!email3) || (!showEmail2 && !!email2 && !!!email3)) &&
        !disabled && (
          <Box sx={{ pb: 3 }}>
            <Button
              variant="text"
              onClick={() => {
                if (!showEmail2 && !!email2) {
                  setShowEmail2(true);
                  return;
                }
                if (!showEmail3 && !!email3) {
                  setShowEmail3(true);
                  return;
                }
              }}
              startIcon={<AddIcon />}
            >
              {translate("addAnother", {
                LABEL: translate("ci_notificationType_Email"),
              })}
            </Button>
          </Box>
        )}
    </>
  );
};
